import { Link } from "react-router-dom";
import { Reveal, routesFor, type Lang } from "../primitives";

/**
 * Bloque compacto de diagnóstico previo. No es otro hero: tres preguntas
 * enlazadas a los servicios que resuelven cada limitante.
 */
const t = {
  es: {
    aria: "Antes de invertir",
    kicker: "Punto de partida",
    lead:
      "Antes de invertir en más tráfico, conviene identificar qué está limitando tu crecimiento: que no te encuentren, que no entiendan tu oferta o que no den el siguiente paso.",
    questions: [
      {
        q: "¿Te encuentran?",
        note: "Rastreo, indexación y arquitectura que permiten aparecer en la búsqueda.",
        key: "serviceTechnical",
        cta: "SEO técnico",
      },
      {
        q: "¿Entienden tu oferta?",
        note: "Contenido y estructura que explican qué haces, para quién y dónde.",
        key: "serviceContent",
        cta: "Contenido y conversión",
      },
      {
        q: "¿Sabes qué genera oportunidades?",
        note: "Medición de contactos, calidad del contacto y origen de la demanda.",
        key: "serviceGoogleAds",
        cta: "Google Ads y medición",
      },
    ],
  },
  en: {
    aria: "Before investing",
    kicker: "Starting point",
    lead:
      "Before investing in more traffic, it pays to identify what is limiting growth: people not finding you, not understanding your offer, or not taking the next step.",
    questions: [
      {
        q: "Can they find you?",
        note: "Crawling, indexing and architecture that let you show up in search.",
        key: "serviceTechnical",
        cta: "Technical SEO",
      },
      {
        q: "Do they understand your offer?",
        note: "Content and structure that explain what you do, for whom and where.",
        key: "serviceContent",
        cta: "Content and conversion",
      },
      {
        q: "Do you know what creates opportunities?",
        note: "Measurement of enquiries, contact quality and where demand comes from.",
        key: "serviceGoogleAds",
        cta: "Google Ads and measurement",
      },
    ],
  },
} as const;

export const SceneIntro = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language) as unknown as Record<string, string>;

  return (
    <section className="v3-section-tight bg-[color:var(--v3-mineral)]" aria-label={c.aria}>
      <div className="v3-shell grid gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-5">
          <p className="v3-micro">{c.kicker}</p>
          <Reveal delay={0.05}>
            <p
              className="v3-display mt-5"
              style={{ fontSize: "clamp(1.3rem, 1.9vw, 1.7rem)", lineHeight: 1.25, fontWeight: 600 }}
            >
              {c.lead}
            </p>
          </Reveal>
        </div>

        <ul className="lg:col-span-7 lg:border-l lg:border-[color:var(--v3-line)] lg:pl-12">
          {c.questions.map((item, i) => (
            <Reveal as="li" key={item.q} delay={0.06 * i}>
              <div className="flex flex-col gap-2 border-b border-[color:var(--v3-line)] py-5 sm:flex-row sm:items-baseline sm:gap-8">
                <p className="min-w-[16rem] text-[1.05rem] font-semibold">{item.q}</p>
                <p className="v3-body flex-1">
                  {item.note}{" "}
                  <Link
                    to={routes[item.key]}
                    className="whitespace-nowrap font-medium underline underline-offset-4"
                    style={{ color: "var(--v3-blue)" }}
                  >
                    {item.cta}
                  </Link>
                </p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};
